import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getMe } from '@/api/client';
import type { AuthUser, Catalog, UploadRecord } from '@/types';

interface AppState {
  user: AuthUser | null;
  product: string | null;
  classification: string | null;
  entity: string | null;
  lastUpload: UploadRecord | null;
  isLoading: boolean;
}

export interface AppContextValue extends AppState {
  setUser: (u: AuthUser | null) => void;
  setProduct: (p: string | null) => void;
  setClassification: (c: string | null) => void;
  setEntity: (e: string | null) => void;
  setLastUpload: (r: UploadRecord | null) => void;
  handleSignout: () => void;
  startOver: () => void;
  catalog: Catalog | null;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [product, setProduct] = useState<string | null>(null);
  const [classification, setClassification] = useState<string | null>(null);
  const [entity, setEntity] = useState<string | null>(null);
  const [lastUpload, setLastUpload] = useState<UploadRecord | null>(null);

  useEffect(() => {
    getMe()
      .then((u) => {
        setUser(u);
        setIsLoading(false);
      })
      .catch(() => {
        setUser(null);
        setIsLoading(false);
      });
  }, []);

  const handleSignout = () => {
    setUser(null);
    setProduct(null);
    setEntity(null);
    setClassification(null);
    setLastUpload(null);
  };

  const startOver = () => {
    setClassification(null);
    setProduct(null);
    setEntity(null);
    setLastUpload(null);
  };
  const catalog = user?.catalog ?? null;
  return (
    <AppContext.Provider
      value={{
        isLoading,
        user,
        setUser,
        classification,
        setClassification,
        product,
        setProduct,
        entity,
        setEntity,
        lastUpload,
        setLastUpload,
        handleSignout,
        startOver,
        catalog,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
